import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDuration, initials } from "@/lib/format";
import { Hourglass } from "lucide-react";

export default function TimeBreakdown({ task, users }) {
  const [data, setData] = useState({ total_seconds: 0, by_user: [] });
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  const refresh = async () => {
    try {
      const { data: d } = await api.get(`/tasks/${task.id}/time-breakdown`);
      setData(d);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, [task.id, task.total_time_seconds]);

  const total = data.total_seconds || 0;
  if (total === 0) {
    return (
      <div className="text-xs text-neutral-400 flex items-center gap-1.5">
        <Hourglass className="w-3.5 h-3.5" /> No time tracked yet.
      </div>
    );
  }

  // Bar segments per user
  const palette = [
    "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500",
    "bg-rose-500", "bg-cyan-500", "bg-orange-500", "bg-pink-500",
  ];

  return (
    <div className="space-y-2" data-testid="time-breakdown">
      <div className="font-mono-data text-sm font-bold text-neutral-900">{formatDuration(total)}</div>
      {/* Stacked bar */}
      <div className="flex h-2 rounded-full overflow-hidden bg-neutral-100">
        {data.by_user.map((row, i) => {
          const pct = (row.total_seconds / total) * 100;
          return (
            <div
              key={row.user_id}
              className={`${palette[i % palette.length]} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${userMap[row.user_id]?.name || "Unknown"} · ${formatDuration(row.total_seconds)}`}
            />
          );
        })}
      </div>
      {/* Per-user rows */}
      <div className="space-y-1.5" data-testid="time-breakdown-rows">
        {data.by_user.map((row, i) => {
          const u = userMap[row.user_id];
          const pct = ((row.total_seconds / total) * 100).toFixed(0);
          return (
            <div key={row.user_id} className="flex items-center gap-2 text-xs" data-testid={`time-row-${row.user_id}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${palette[i % palette.length]}`} />
              <div className="w-5 h-5 rounded-full bg-neutral-950 text-white text-[9px] font-bold flex items-center justify-center shrink-0">
                {initials(u?.name)}
              </div>
              <span className="font-medium flex-1 truncate">{u?.name || "Unknown"}</span>
              <span className="font-mono-data text-neutral-700">{formatDuration(row.total_seconds)}</span>
              <span className="font-mono-data text-[10px] text-neutral-400 w-8 text-right">{pct}%</span>
              <span className="font-mono-data text-[10px] text-neutral-400">{row.entries}×</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
